/**
 * Google Analytics 4 — env-gated, prod-default. (Inlined, NOT @gjl/analytics.)
 *
 * WHY STILL INLINED (revised 2026-08-12). The original reason was that a
 * project-level `npm ci` plus tailwind v4 meant adding a @gjl *workspace* dep
 * broke `@tailwindcss/vite`'s `tailwindcss` resolution — two prod deploys failed
 * 2026-06-06. That reason is now STALE: the CI installs at root
 * (`npm ci --include-workspace-root`), this client is a listed workspace, and it
 * already depends on @gjl/motion and @gjl/site-kit. Workspace deps are fine.
 *
 * It stays inlined for a different reason: @gjl/analytics is a provider
 * abstraction with its own gtag typings, and migrating means reconciling the two.
 * That is a real change, not a drop-in, and it is not worth bundling into an
 * unrelated one. The path is still open when someone wants it.
 *
 * What DID bite (2026-08-12): this module used to `declare global` the gtag
 * window properties. The moment @gjl/site-kit entered the type graph for
 * prerendering, @gjl/analytics's competing declaration collided (TS2687/TS2717)
 * and the build stopped. Hence the local type view below rather than a global.
 *
 * The fairmeadow.capital GA4 web stream. A Measurement ID is public (sent to the
 * browser), so it's baked as the prod default; .env is gitignored from CI.
 */

const GA_ID = (import.meta.env.VITE_GA_ID as string | undefined) || (import.meta.env.PROD ? 'G-CYSL89QH6Z' : undefined)

/**
 * Local view of the gtag globals, NOT a `declare global`.
 *
 * This module used to widen `Window` globally. That collided the moment
 * @gjl/site-kit entered the type graph (for prerendering), because
 * @gjl/analytics declares the same two properties with different modifiers and
 * a stricter gtag signature — TS2687/TS2717, and the build stops. Two modules
 * cannot both own a global interface.
 *
 * Keeping the view local means the inlined module stays self-contained and
 * cannot conflict with whatever else lands in the graph later.
 */
// Intersected with Window, so narrowing to it is a SINGLE assertion. A bare
// `{dataLayer, gtag}` shape would need `as unknown as`, which the repo lint rule
// `no-restricted-syntax` bans outright, and rightly — a double cast asserts two
// unrelated things at once.
type GtagWindow = Window & {
  dataLayer: unknown[]
  gtag: (...args: unknown[]) => void
}

/** The window, viewed as a gtag host. Call only after a `typeof window` guard. */
function gtagWindow(): GtagWindow {
  return window as GtagWindow
}

let initialized = false

/** Inject gtag.js and configure GA4. Safe to call once at startup. */
export function initAnalytics(): void {
  if (initialized || !GA_ID || typeof window === 'undefined') return
  initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  const w = gtagWindow()
  w.dataLayer = w.dataLayer || []
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments)
  }
  w.gtag('js', new Date())
  w.gtag('config', GA_ID, { send_page_view: false })
}

/** Record a page view for the given SPA path. No-op when analytics is off. */
export function trackPageview(path: string): void {
  if (!GA_ID || typeof window === 'undefined') return
  const w = gtagWindow()
  if (typeof w.gtag !== 'function') return
  w.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

/** True when a measurement ID is configured (analytics is live). */
export const analyticsEnabled = Boolean(GA_ID)
